/**
 * Warstwa logiki zgód (Consent Mode v2).
 *
 * Wartości domyślne ustawia inline'owy skrypt w index.html — musi wykonać się
 * przed GTM. Ten moduł odpowiada wyłącznie za to, co dzieje się PO decyzji
 * użytkownika: wywołanie `gtag('consent','update')`, zapis wyboru i
 * poinformowanie reszty aplikacji.
 */

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export type ConsentSignal =
  | 'ad_storage'
  | 'ad_user_data'
  | 'ad_personalization'
  | 'analytics_storage'
  | 'functionality_storage'
  | 'personalization_storage'
  | 'security_storage';

export type ConsentValue = 'granted' | 'denied';
export type ConsentState = Record<ConsentSignal, ConsentValue>;

/** Który przycisk w modalu doprowadził do zapisania decyzji. */
export type ConsentChoice = 'accept_all' | 'reject_all' | 'custom' | 'stored';

export interface StoredConsent {
  version: number;
  updatedAt: string;
  choice: ConsentChoice;
  signals: ConsentState;
}

export const CONSENT_KEY = 'nf_consent';

/**
 * Podbicie tej wartości unieważnia zapisane zgody i pokazuje modal ponownie.
 * Rób to, gdy zmienią się kategorie albo cele przetwarzania.
 */
export const CONSENT_VERSION = 1;

export interface ConsentCategory {
  id: string;
  label: string;
  description: string;
  /** Kategoria niezbędna — nie da się jej wyłączyć. */
  required?: boolean;
  signals: ConsentSignal[];
}

export const CATEGORIES: ConsentCategory[] = [
  {
    id: 'necessary',
    label: 'Niezbędne',
    description:
      'Konieczne do działania sklepu: utrzymanie zawartości koszyka, sesja logowania, zabezpieczenia przed nadużyciami. Bez nich sklep nie zadziała, dlatego nie można ich wyłączyć.',
    required: true,
    signals: ['security_storage'],
  },
  {
    id: 'functional',
    label: 'Funkcjonalne',
    description:
      'Zapamiętywanie Twoich preferencji, na przykład ostatnio oglądanych produktów czy sposobu sortowania w katalogu.',
    signals: ['functionality_storage', 'personalization_storage'],
  },
  {
    id: 'analytics',
    label: 'Analityczne',
    description:
      'Anonimowe statystyki odwiedzin: które produkty są oglądane, gdzie użytkownicy przerywają zakupy. Pomagają nam poprawiać sklep.',
    signals: ['analytics_storage'],
  },
  {
    id: 'marketing',
    label: 'Marketingowe',
    description:
      'Mierzenie skuteczności reklam i wyświetlanie Ci dopasowanych treści w serwisach reklamowych Google.',
    signals: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  },
];

const ALL_SIGNALS = CATEGORIES.flatMap((c) => c.signals);

function buildState(value: ConsentValue): ConsentState {
  const state = {} as ConsentState;
  for (const signal of ALL_SIGNALS) state[signal] = value;
  // Kategoria niezbędna jest zawsze aktywna, niezależnie od wyboru.
  for (const cat of CATEGORIES) if (cat.required) for (const s of cat.signals) state[s] = 'granted';
  return state;
}

export const DENY_ALL: ConsentState = buildState('denied');
export const GRANT_ALL: ConsentState = buildState('granted');

/** Zamienia zaznaczone kategorie na komplet sygnałów Consent Mode. */
export function categoriesToSignals(selected: Record<string, boolean>): ConsentState {
  const state = { ...DENY_ALL };
  for (const cat of CATEGORIES) {
    if (cat.required || selected[cat.id]) {
      for (const s of cat.signals) state[s] = 'granted';
    }
  }
  return state;
}

/** Odwrotność powyższego — do wstępnego zaznaczenia przełączników. */
export function signalsToCategories(signals: ConsentState): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const cat of CATEGORIES) {
    out[cat.id] = cat.required ? true : cat.signals.every((s) => signals[s] === 'granted');
  }
  return out;
}

export function readConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    // Starsza wersja zgód = pytamy ponownie.
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Zapisuje decyzję użytkownika i przekazuje ją do Google.
 *
 * Kolejność ma znaczenie: najpierw `gtag('consent','update')`, żeby tagi
 * czekające na zgodę mogły odpalić, dopiero potem zdarzenie do dataLayer.
 */
export function saveConsent(signals: ConsentState, choice: ConsentChoice): void {
  window.gtag?.('consent', 'update', signals);

  const stored: StoredConsent = {
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    choice,
    signals,
  };

  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(stored));
  } catch {
    /* tryb prywatny — decyzja obowiązuje do końca sesji */
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'consent_update',
    consent_source: 'banner',
    consent_choice: choice,
    ...Object.fromEntries(Object.entries(signals).map(([k, v]) => [`consent_${k}`, v])),
  });

  window.dispatchEvent(new CustomEvent<StoredConsent>('nf:consent', { detail: stored }));
}

/** Usuwa zapisaną decyzję i pokazuje modal od nowa. */
export function revokeConsent(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event('nf:consent-open'));
}

/** Otwiera modal na ekranie szczegółowych ustawień. */
export function openConsentSettings(): void {
  window.dispatchEvent(new Event('nf:consent-open'));
}
